import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users,
  Building2,
  Newspaper,
  Handshake,
  LogOut,
  Menu,
  X,
  Home,
  ClipboardList,
  UsersRound,
  LayoutDashboard,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const navItems = [
  { path: '/admin/dashboard', label: 'Painel Executivo', icon: LayoutDashboard, testId: 'sidebar-dashboard' },
  { path: '/admin/leads', label: 'Leads', icon: Users, testId: 'sidebar-leads' },
  { path: '/admin/companies', label: 'Empresas', icon: Building2, testId: 'sidebar-companies' },
  { path: '/admin/personnel', label: 'Quadro de Pessoal', icon: UsersRound, testId: 'sidebar-personnel' },
  { path: '/admin/assessments', label: 'Diagnósticos', icon: ClipboardList, testId: 'sidebar-assessments' },
  { path: '/admin/news', label: 'Notícias', icon: Newspaper, testId: 'sidebar-news' },
  { path: '/admin/partners', label: 'Parceiros', icon: Handshake, testId: 'sidebar-partners' },
];

export const AdminSidebar = ({ children, title, subtitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    navigate('/');
    await logout();
  };

  const isActive = (path) => location.pathname === path;

  const sidebarInner = (
    <>
      <div className="p-5 border-b border-teal-800/40">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white hover:text-teal-200 transition-colors"
          data-testid="sidebar-home"
        >
          <Home size={16} aria-hidden="true" />
          <span className="text-sm font-medium">Ver site público</span>
        </button>
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-teal-300 font-bold">
            Admin
          </div>
          <div className="text-sm text-white/90 mt-1 truncate">{user?.name || user?.email}</div>
        </div>
      </div>

      <nav className="flex-1 py-4" aria-label="Menu do painel administrativo">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              data-testid={item.testId}
              className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors border-l-4 ${
                active
                  ? 'bg-teal-800 border-teal-300 text-white font-semibold'
                  : 'border-transparent text-teal-100 hover:bg-teal-800/60 hover:text-white'
              }`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={18} aria-hidden="true" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-teal-800/40">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-800 hover:bg-teal-950 text-white text-sm font-medium transition-colors"
          data-testid="sidebar-logout"
        >
          <LogOut size={16} aria-hidden="true" />
          Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-teal-900 text-white flex-col fixed inset-y-0 left-0 z-30">
        {sidebarInner}
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-teal-900 text-white flex flex-col">
            <div className="flex justify-end p-3">
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-white hover:text-teal-200"
                aria-label="Fechar menu"
              >
                <X size={22} />
              </button>
            </div>
            {sidebarInner}
          </aside>
        </>
      )}

      {/* Main area */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="px-4 md:px-8 py-4 flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 text-gray-700 hover:text-teal-600"
              aria-label="Abrir menu"
            >
              <Menu size={22} />
            </button>
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">{title}</h1>
              {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
          </div>
        </header>

        <main className="p-4 md:p-8 max-w-7xl">{children}</main>
      </div>
    </div>
  );
};

export default AdminSidebar;
